import { useCallback, useEffect, useRef, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { AnnualWheelActivitySaveModel } from "../../../../annualWheel/AnnualWheelActivity.types";
import { ActivityFormModel, ActivityFormSection, ActivityFormValues } from "../../../../annualWheel/activityForm/ActivityFormModel";
import { ActivityScheduleValues } from "../../../../annualWheel/activityForm/ActivityScheduleModel";
import { ContractBasicInfo } from "../../../../annualWheel/RelativeDate.types";
import { ObligationCreationProgress, ObligationExtractionResultViewModel, ObligationPageStatus, ObligationSuggestionPage } from "./ObligationExtraction.types";
import { SelectedTextResult } from "@common/components/documentPreview/textSelection/textSelection.types";
import { suggestionToPage } from "./obligationSuggestionAdapter";
import { useTranslation } from "../../../../localization/useTranslation";

const ANNUAL_WHEEL_URL = "/annualWheelActivity/";

export interface IUseObligationExtractionPanelProps {
    contractId: string;
    /** Undefined while the previewer this panel attaches to has not settled on a document yet. */
    documentId: string | undefined;
    /** Starts the scan as soon as the panel opens, for the entry point that already picked a document. */
    autoScan: boolean;
    onCreated: () => void;
}

export function useObligationExtractionPanel(props: IUseObligationExtractionPanelProps) {
    const [pages, setPages] = useState<Array<ObligationSuggestionPage>>([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [progress, setProgress] = useState<ObligationCreationProgress>({ isCreating: false, createdCount: 0, totalCount: 0 });
    // The scan outcome cannot be derived from the mutation: suggestions become editable models the user
    // then changes, and the shared mutation result exposes no success or error flag of its own.
    const [scanStatus, setScanStatus] = useState<"notStarted" | "succeeded" | "failed">("notStarted");
    const [validationAttempt, setValidationAttempt] = useState(0);
    // Lets the user step back out of the completion screen into the review. Only reachable after a partial
    // failure, where the obligation that could not be created may need editing before it will succeed.
    const [hasReturnedToReview, setHasReturnedToReview] = useState(false);

    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const { data: contractInfo } = useDotLegalQuery<ContractBasicInfo>(`/contracts/${props.contractId}/basic-info`, { enabled: !!props.contractId });

    const scanMutation = useDotLegalMutation<Result<ObligationExtractionResultViewModel>, string | undefined>(
        (guidance) =>
            post<ObligationExtractionResultViewModel>(`/contracts/${props.contractId}/documents/${props.documentId}/extract-obligations`, { guidance }),
        {
            onSuccess: (response: Result<ObligationExtractionResultViewModel>) => {
                const result = response.value();
                setPages(result.suggestions.map((suggestion) => suggestionToPage(suggestion, props.contractId, result.contractDocumentId, contractInfo)));
                setPageIndex(0);
                setProgress({ isCreating: false, createdCount: 0, totalCount: 0 });
            },
            onSettled: (_data, error) => setScanStatus(error ? "failed" : "succeeded"),
        }
    );

    function scan(guidance?: string) {
        if (props.documentId) {
            scanMutation.mutate(guidance);
        }
    }

    useEffect(() => {
        if (props.autoScan && scanStatus === "notStarted" && !scanMutation.isLoading) {
            scan();
        }
        // eslint-disable-next-line
    }, [props.autoScan, props.documentId]);

    /**
     * Drops a finished review so the panel can be reused for a different document without remounting.
     * Stable so a caller can depend on it from an effect - every setter it calls is itself stable.
     */
    const reset = useCallback(() => {
        setPages([]);
        setPageIndex(0);
        setProgress({ isCreating: false, createdCount: 0, totalCount: 0 });
        setScanStatus("notStarted");
        setHasReturnedToReview(false);
    }, []);

    /** Leaves the completion screen and puts the user back on the obligation that could not be created. */
    function backToReview() {
        setHasReturnedToReview(true);
        if (progress.failedPageIndex !== undefined) {
            setPageIndex(progress.failedPageIndex);
        }
    }

    /**
     * Discards the current review and asks the model again. Costs another scan, and every edit the user
     * made to the suggestions is lost, so the control that calls this has to say so up front.
     * Without guidance the run is deterministic and returns the same obligations, so the message the user
     * types is the only thing that makes a second scan worth paying for.
     */
    function rescan(guidance?: string) {
        reset();
        scan(guidance);
    }

    const currentPage = pages[pageIndex];
    const includedPages = pages.filter((page) => !page.excluded);
    // The obligations a create run would actually post. Distinct from includedPages, which still counts the
    // ones already created: a button gated on that count stays pressable with nothing left to do, and the
    // run then reports success having created nothing.
    const outstandingIndexes = pages
        .map((page, index) => ({ page, index }))
        .filter((entry) => !entry.page.excluded && !entry.page.created)
        .map((entry) => entry.index);
    const isLastPage = pages.length > 0 && pageIndex === pages.length - 1;

    function updateCurrentPage(updates: Partial<ObligationSuggestionPage>) {
        setPages((previous) => previous.map((page, index) => (index === pageIndex ? { ...page, ...updates } : page)));
    }

    function onCurrentPageModelChange(updates: Partial<ActivityFormValues>) {
        updateCurrentPageModel((current) => current.withUpdates(updates));
    }

    function onCurrentPageScheduleChange(updates: Partial<ActivityScheduleValues>) {
        updateCurrentPageModel((current) => current.withUpdates({ schedule: current.schedule.withUpdates(updates) }));
    }

    function updateCurrentPageModel(update: (current: ActivityFormModel) => ActivityFormModel) {
        setPages((previous) => previous.map((page, index) => (index === pageIndex ? { ...page, model: update(page.model) } : page)));
    }

    function getFirstInvalidSection(page: ObligationSuggestionPage): ActivityFormSection | undefined {
        return page.model.getFirstInvalidSection();
    }

    function isPageValid(page: ObligationSuggestionPage) {
        return getFirstInvalidSection(page) === undefined;
    }

    // Bumped on every rejected attempt so a second press of Next scrolls again, even when the same
    // section is still the offending one.
    function rejectPage(index: number) {
        setPages((previous) => previous.map((page, pageIndex) => (pageIndex === index ? { ...page, showError: true } : page)));
        setValidationAttempt((previous) => previous + 1);
    }

    function goToNextPage() {
        if (!currentPage.excluded && !isPageValid(currentPage)) {
            rejectPage(pageIndex);
            return;
        }

        updateCurrentPage({ showError: false });
        setPageIndex((previous) => Math.min(previous + 1, pages.length - 1));
    }

    /**
     * Free navigation, used by the pager. Unlike Next it never refuses, but walking away from an
     * unfinished obligation is what turns it red - the user has now seen it and moved on.
     */
    function goToPage(targetIndex: number) {
        if (targetIndex === pageIndex || targetIndex < 0 || targetIndex >= pages.length) {
            return;
        }

        setPages((previous) => previous.map((page, index) => (index === pageIndex ? { ...page, showError: !page.excluded && !isPageValid(page) } : page)));
        setPageIndex(targetIndex);
    }

    function goToPreviousPage() {
        goToPage(pageIndex - 1);
    }

    function setPage(requestedPage: number) {
        goToPage(requestedPage - 1);
    }

    function getPageStatus(page: ObligationSuggestionPage): ObligationPageStatus {
        if (page.created) {
            return "created";
        }
        if (page.excluded) {
            return "excluded";
        }
        if (isPageValid(page)) {
            return "ready";
        }

        return page.showError ? "needsAttention" : "untouched";
    }

    // An obligation is resolved when it is excluded or complete. No separate "decided" flag is needed:
    // responsible is seeded empty, so a page cannot become valid without the user having acted on it.
    const firstUnresolvedIndex = pages.findIndex((page) => !page.excluded && !page.created && !isPageValid(page));

    // Separate from the above on purpose. Every obligation starts incomplete, so counting all of them as
    // problems would greet the user with a wall of red before they had done anything wrong. Only pages
    // they have already walked away from, or that a blocked create has called out, count as problems.
    const firstNeedsAttentionIndex = pages.findIndex((page) => getPageStatus(page) === "needsAttention");

    /**
     * The user pointed at the real clause in the document beside the panel. Section comes from the same
     * heuristic the annual wheel dialog uses, and an empty one clears the field rather than leaving the
     * model's stale guess beside a clause it no longer describes.
     */
    function applySelectedClause(result: SelectedTextResult) {
        updateCurrentPage({
            model: currentPage.model.withUpdates({ clause: result.clause, section: result.section ?? "" }),
            clauseReplaced: true,
        });
    }

    function toggleExcluded() {
        const isExcluding = !currentPage.excluded;
        updateCurrentPage({ excluded: isExcluding, showError: false });

        // Excluding settles this obligation, so the review moves on; including it is the start of filling it
        // in, so the user stays. goToPage rather than goToNextPage because Next validates the page it leaves
        // and would close over the stale currentPage, rejecting the very page just excluded. Both setPages
        // calls are functional updaters applied in order, so goToPage already sees the page as excluded and
        // leaves showError false - it does not flash red on its way out.
        if (isExcluding) {
            goToPage(pageIndex + 1);
        }
    }

    const createMutation = useDotLegalMutation((saveModel: AnnualWheelActivitySaveModel) => post<{}>(ANNUAL_WHEEL_URL, saveModel));

    // One at a time on purpose: each create reads the current max task order on the server, so concurrent
    // creates would read the same value and collide.
    async function createNext(outstanding: Array<number>, position: number, createdCount: number) {
        if (position >= outstanding.length) {
            setProgress({ isCreating: false, createdCount, totalCount: outstanding.length });
            snackbar.show(translateString("obligationsCreatedSuccessfully"));
            props.onCreated();
            return;
        }

        const pageIndexToCreate = outstanding[position];

        try {
            await createMutation.mutateAsync(pages[pageIndexToCreate].model.toSaveModel(undefined));
        } catch {
            setProgress({ isCreating: false, createdCount, totalCount: outstanding.length, failedPageIndex: pageIndexToCreate });
            setPageIndex(pageIndexToCreate);
            // Whatever succeeded before the failure is real and has to reach the list behind the panel.
            if (createdCount > 0) {
                props.onCreated();
            }
            return;
        }

        setPages((previous) => previous.map((page, index) => (index === pageIndexToCreate ? { ...page, created: true } : page)));
        setProgress({ isCreating: true, createdCount: createdCount + 1, totalCount: outstanding.length });

        await createNext(outstanding, position + 1, createdCount + 1);
    }

    function addObligations() {
        // The real gate. Navigation is free, so this is the one place that insists every obligation has
        // been either completed or excluded, and it reveals all the outstanding ones at once.
        if (firstUnresolvedIndex >= 0) {
            setPages((previous) => previous.map((page) => (!page.excluded && !page.created && !isPageValid(page) ? { ...page, showError: true } : page)));
            setPageIndex(firstUnresolvedIndex);
            setValidationAttempt((previous) => previous + 1);
            // Leaves the completion screen when this came from Try again, so the errors just flipped on are
            // on a page the user can see. Staying there would make the button look dead.
            setHasReturnedToReview(true);
            return;
        }

        // Only what is still outstanding, so pressing the button again after a partial failure does not
        // create the successful ones a second time.
        setHasReturnedToReview(false);
        setProgress({ isCreating: true, createdCount: 0, totalCount: outstandingIndexes.length });
        createNext(outstandingIndexes, 0, 0);
    }

    const isTerminalRun = !progress.isCreating && progress.totalCount > 0 && !hasReturnedToReview;

    return {
        contractInfo,
        pages,
        pageIndex,
        currentPage,
        isLastPage,
        hasScanned: scanStatus === "succeeded",
        isScanning: scanMutation.isLoading,
        hasScanFailed: scanStatus === "failed",
        scan,
        rescan,
        reset,
        includedCount: includedPages.length,
        // What a create run would post. Gate any button that starts one on this, never on includedCount.
        outstandingCount: outstandingIndexes.length,
        progress,
        // A run has been attempted and is no longer in flight. totalCount counts only the pages that run
        // set out to create, so it is not a cumulative total - it is only ever used as "a run happened".
        isTerminal: isTerminalRun,
        // The edited name rather than the model's, because that is what was actually posted.
        createdNames: pages.filter((page) => page.created).map((page) => page.model.name),
        failedName: progress.failedPageIndex !== undefined ? pages[progress.failedPageIndex]?.model.name : undefined,
        backToReview,
        updateCurrentPage,
        onCurrentPageModelChange,
        onCurrentPageScheduleChange,
        goToNextPage,
        goToPreviousPage,
        setPage,
        toggleExcluded,
        applySelectedClause,
        // Nothing to write a picked clause into while the scan is still running, after it failed or found
        // nothing, or once the run has finished and the forms are gone.
        canApplySelectedClause: currentPage !== undefined && !isTerminalRun,
        addObligations,
        currentClause: currentPage?.model.clause,
        // A hand-picked clause is verifiably in the document, whatever the locator originally decided.
        currentClauseLocated: currentPage ? currentPage.suggestion.clauseLocated || currentPage.clauseReplaced : true,
        firstInvalidSection: currentPage?.showError ? getFirstInvalidSection(currentPage) : undefined,
        validationAttempt,
        goToPage,
        pageStatuses: pages.map(getPageStatus),
        firstUnresolvedIndex: firstUnresolvedIndex >= 0 ? firstUnresolvedIndex : undefined,
        firstNeedsAttentionIndex: firstNeedsAttentionIndex >= 0 ? firstNeedsAttentionIndex : undefined,
        counts: {
            ready: pages.filter((page) => getPageStatus(page) === "ready").length,
            excluded: pages.filter((page) => getPageStatus(page) === "excluded").length,
            untouched: pages.filter((page) => getPageStatus(page) === "untouched").length,
            needsAttention: pages.filter((page) => getPageStatus(page) === "needsAttention").length,
            created: pages.filter((page) => getPageStatus(page) === "created").length,
        },
    };
}

/**
 * Puts a new obligation at the top of the panel. The document beside it already re-scrolls to the new
 * clause, so leaving the panel where it was left the two panes reading different obligations. Instant
 * rather than smooth: this is a context switch, not movement within one form.
 */
export function useScrollToTopOnPageChange(pageIndex: number, validationAttempt: number) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const lastValidationAttempt = useRef(validationAttempt);

    useEffect(() => {
        // Add obligations jumps to the first unresolved page and scrolls to the field that blocked it, so
        // both effects fire on the same commit. That target beats the top, so this one stands down.
        const isValidationJump = lastValidationAttempt.current !== validationAttempt;
        lastValidationAttempt.current = validationAttempt;

        if (isValidationJump) {
            return;
        }

        bodyRef.current?.scrollTo({ top: 0 });
    }, [pageIndex, validationAttempt]);

    return bodyRef;
}

export type ObligationExtractionPanelState = ReturnType<typeof useObligationExtractionPanel>;
